import { productPath } from '../services/productPath';
import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Star, ShoppingCart, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import BrandLogo from './BrandLogo';
import { productsAPI, cartAPI } from '../services/api';
import { vehicleStore, useSelectedVehicle } from '../services/vehicleStore';
import { useT } from '../services/i18n';
import { prodName, useTerms } from '../services/catalogI18n';

const BestSellers = () => {
  const { t, lang } = useT();
  const tt = useTerms(lang);
  const scrollRef = useRef(null);
  const vehicle = useSelectedVehicle();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(null);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await productsAPI.getAll(null, null, vehicleStore.toParams(vehicle));
        setProducts(data);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadProducts();
  }, [vehicle]);

  const handleAddToCart = async (productId) => {
    
    setAddingToCart(productId);
    try {
      await cartAPI.addItem(productId);
      alert(t('home.addedToCart'));
    } catch (error) {
      alert(t('home.addError'));
    } finally {
      setAddingToCart(null);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (isLoading) {
    return (
      <section className="py-6 sm:py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-6 sm:py-10 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6 gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              {vehicle
                ? t('home.compatibleParts', { vehicle: `${vehicle.brand} ${vehicle.model}` })
                : t('home.bestsellers')}
            </h2>
            {vehicle && (
              <p className="text-sm text-gray-500 mt-1" data-testid="bestsellers-vehicle-hint">
                <CheckCircle2 size={14} className="inline text-green-600 mr-1" />
                {t('home.compatibleCount', { n: products.length })} · {vehicle.engine?.name} ·{' '}
                <Link to="/pieces" className="text-blue-600 hover:underline" data-testid="bestsellers-see-all">{t('home.seeAllCompatible')}</Link>
              </p>
            )}
          </div>
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="p-2 rounded-full border border-gray-300 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll('right')}
              className="p-2 rounded-full border border-gray-300 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto pb-4 scrollbar-hide scroll-smooth snap-x snap-mandatory -mx-4 px-4 sm:mx-0 sm:px-0"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {products.map((product) => (
            <div
              key={product.id}
              className="relative flex-shrink-0 w-56 sm:w-64 snap-start bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer group"
            >
              {/* Discount badge */}
              {product.discount && (
                <div className="absolute top-2 left-2 z-10">
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded" data-testid={`bestseller-discount-${product.id}`}>
                    -{product.discount}%
                  </span>
                </div>
              )}

              {/* Product image */}
              <Link to={productPath(product)} data-testid={`bestseller-link-${product.id}`} className="h-40 mb-3 flex items-center justify-center">
                <img
                  src={product.image}
                  alt={prodName(product, lang)}
                  className="max-h-full max-w-full object-contain transition-transform duration-200 group-hover:scale-105"
                />
              </Link>

              {/* Rating */}
              <div className="flex items-center gap-1 mb-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={14}
                      className={i < Math.floor(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                    />
                  ))}
                </div>
                <span className="text-xs text-gray-500">({product.reviews})</span>
              </div>

              {/* Brand */}
              <p className="text-xs text-gray-500 font-medium"><BrandLogo brand={product.brand} size="xs" testId={`bestseller-brand-${product.id}`} /></p>

              {/* Product name */}
              <h3 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2 group-hover:text-orange-600 transition-colors">
                <Link to={productPath(product)}>{prodName(product, lang)}</Link>
              </h3>

              {/* Reference */}
              <p className="text-xs text-gray-400 mb-2">Réf.: {product.ref}</p>

              {/* Type */}
              <p className="text-xs text-gray-500 mb-3 line-clamp-1">{tt(product.type)}</p>

              {/* Price */}
              <div className="flex items-baseline gap-2 mb-3">
                {product.original_price && (
                  <span className="text-sm text-gray-400 line-through">
                    {product.original_price.toFixed(2)} €
                  </span>
                )}
                <span className="text-xl font-bold text-gray-900">
                  {product.price.toFixed(2)} €
                </span>
              </div>

              {/* Add to cart */}
              <Button 
                className="w-full bg-orange-500 hover:bg-orange-600 text-sm"
                onClick={() => handleAddToCart(product.id)}
                disabled={addingToCart === product.id}
              >
                {addingToCart === product.id ? (
                  <Loader2 size={16} className="animate-spin mr-2" />
                ) : (
                  <ShoppingCart size={16} className="mr-2" />
                )}
                {t('common.addToCart')}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BestSellers;
